import { OfflinePaymentDetails, PaymentItem } from '../types';
import { OFFLINE_PAYMENT_DETAILS } from '../config/flutterwave';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { calculateReferralCommission } from './referralCommission';

// Create offline payment record in Firebase
export const createOfflinePayment = async (
  userId: string,
  items: PaymentItem[],
  total: number,
  customerInfo: any,
  offlinePaymentMethod: "MTN Mobile Money" | "MoMo Pay" | "ABSA Bank",
  referredBy?: string
): Promise<string> => {
  try {
    // Validate inputs
    if (!userId || !items || items.length === 0 || !total || !customerInfo || !offlinePaymentMethod) {
      throw new Error('Missing required parameters for offline payment');
    }

    const paymentId = `OFFLINE_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Build payment details based on method
    const paymentDetails: any = {
      method: offlinePaymentMethod,
      accountName: OFFLINE_PAYMENT_DETAILS.mtnMobileMoney.accountName,
    };

    if (offlinePaymentMethod === 'ABSA Bank') {
      paymentDetails.accountNumber = OFFLINE_PAYMENT_DETAILS.absaBank.accountNumber;
    } else {
      paymentDetails.phoneNumber = OFFLINE_PAYMENT_DETAILS.mtnMobileMoney.phoneNumber;
    }

    const offlinePayment: OfflinePaymentDetails = {
      id: paymentId,
      userId,
      items,
      total,
      currency: 'GHS',
      customerInfo,
      paymentMethod: 'offline',
      paymentDetails,
      referredBy,
      status: 'pending',
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    // Clean the data before sending to Firestore (remove any undefined values)
    const cleanOfflinePayment = JSON.parse(JSON.stringify(offlinePayment));
    
    // Save to OfflinePayment collection
    const offlinePaymentRef = doc(db, 'OfflinePayment', paymentId);
    await setDoc(offlinePaymentRef, cleanOfflinePayment);

    return paymentId;
  } catch (error) {
    console.error('Error creating offline payment:', error);
    throw new Error('Failed to create offline payment record');
  }
};

// Redirect to WhatsApp with payment notification
export const redirectToWhatsApp = (paymentId: string, total: number, items: PaymentItem[], customerInfo: any, referredBy?: string) => {
  let message = `Hello! I have made payment for my order.

📋 ORDER DETAILS:
Payment ID: ${paymentId}
Amount: GHS ${total.toFixed(2)}
Items: ${items.length} item(s)

👤 CUSTOMER INFORMATION:
Name: ${customerInfo.firstName} ${customerInfo.lastName}
Email: ${customerInfo.email}
Phone: ${customerInfo.phone}
Address: ${customerInfo.address}, ${customerInfo.city}, ${customerInfo.state}, ${customerInfo.country}

🛒 ITEMS PURCHASED:`;

  // Add each item with details
  items.forEach((item, index) => {
    message += `\n${index + 1}. ${item.name} (${item.type}) - GHS ${item.amount.toFixed(2)}`;
  });

  // Add referral information if available
  if (referredBy) {
    message += `\n\n🎯 Referred By: ${referredBy}`;
    
    // Calculate and add commission information for display (commission will be processed after payment verification)
    const commissionResult = calculateReferralCommission(items, referredBy);
    if (commissionResult) {
      const totalAmount = items.reduce((sum, item) => sum + item.amount, 0);
      const commissionRate = (commissionResult.commissionAmount / totalAmount) * 100;
      
      message += `\n💰 Referral Commission: GHS ${commissionResult.commissionAmount.toFixed(2)} (${commissionRate.toFixed(0)}%)`;
      message += `\n📊 Commission Breakdown:`;
      commissionResult.commissionDetails.forEach((detail, index) => {
        message += `\n   ${index + 1}. ${detail.itemName} (${detail.itemType}): GHS ${detail.commissionAmount.toFixed(2)}`;
      });
      message += `\n⚠️ Note: Commission will be processed after payment verification`;
    }
  }

  message += `\n\nPlease verify and grant access to my courses/books.`;
  
  try {
    // Use the standard WhatsApp API format that works reliably
    const whatsappUrl = `https://api.whatsapp.com/send?phone=${OFFLINE_PAYMENT_DETAILS.whatsappNumber}&text=${encodeURIComponent(message)}`;
    
    // Try to open WhatsApp
    const newWindow = window.open(whatsappUrl, '_blank');
    
    // If window.open fails, try alternative approach
    if (!newWindow) {
      // Fallback: copy message to clipboard and show instructions
      navigator.clipboard.writeText(message).then(() => {
        alert(`WhatsApp couldn't open automatically. I've copied the message to your clipboard. Please:\n\n1. Open WhatsApp manually\n2. Send the copied message to ${OFFLINE_PAYMENT_DETAILS.whatsappNumber}`);
      }).catch(() => {
        // If clipboard fails, just show the message
        alert(`WhatsApp couldn't open automatically. Please manually send this message to ${OFFLINE_PAYMENT_DETAILS.whatsappNumber}:\n\n${message}`);
      });
    }
  } catch (error) {
    console.error('Error opening WhatsApp:', error);
    // Fallback: show message in alert
    alert(`Please manually send this message to ${OFFLINE_PAYMENT_DETAILS.whatsappNumber}:\n\n${message}`);
  }
};

// Get payment details for display
export const getPaymentDetails = (method: "MTN Mobile Money" | "MoMo Pay" | "ABSA Bank") => {
  switch (method) {
    case 'MTN Mobile Money':
      return OFFLINE_PAYMENT_DETAILS.mtnMobileMoney;
    case 'MoMo Pay':
      return OFFLINE_PAYMENT_DETAILS.momoPay;
    case 'ABSA Bank':
      return OFFLINE_PAYMENT_DETAILS.absaBank;
    default:
      return OFFLINE_PAYMENT_DETAILS.mtnMobileMoney;
  }
};

// Format payment instructions
export const getPaymentInstructions = (method: "MTN Mobile Money" | "MoMo Pay" | "ABSA Bank") => {
  switch (method) {
    case 'MTN Mobile Money':
      return `1. Open MTN Mobile Money app\n2. Select "Send Money"\n3. Enter phone number: ${OFFLINE_PAYMENT_DETAILS.mtnMobileMoney.phoneNumber}\n4. Enter recipient name: ${OFFLINE_PAYMENT_DETAILS.mtnMobileMoney.accountName}\n5. Complete transaction`;
    
    case 'MoMo Pay':
      return `1. Open MoMo Pay app\n2. Select "Send Money"\n3. Enter phone number: ${OFFLINE_PAYMENT_DETAILS.momoPay.phoneNumber}\n4. Enter recipient name: ${OFFLINE_PAYMENT_DETAILS.momoPay.accountName}\n5. Complete transaction`;
    
    case 'ABSA Bank':
      return `1. Open your bank app\n2. Select "Transfer"\n3. Enter account number: ${OFFLINE_PAYMENT_DETAILS.absaBank.accountNumber}\n4. Enter recipient name: ${OFFLINE_PAYMENT_DETAILS.absaBank.accountName}\n5. Complete transaction`;
    
    default:
      return 'Please select a payment method';
  }
};
